import React from 'react';
import {useSelector} from 'react-redux';

import {labels} from '../../../../../../util/utils';

import normalizeContactList from '../../GenerateMessage/GMForm/utils/normalizeContactList';
import getAvailableEntityList from '../../GenerateMessage/GMForm/utils/getAvailableEntityList';

import OutputText from '@ey/canvascoreservices/OutputText';
import MotifAccordionContent from '@ey/motif-react-wrapper/AccordionContent';

import StyledMessageAccordionContent from './StyledMessageAccordionContent';
import SeeMore from '../SeeMore';

const MessageAccordionContent = ({
	subject,
	contactsList,
	entitiesList,
	countriesList,
	groupRolesList,
	engagementsList,
	serviceLineList,
	rolesList
}) => {
	const storeContactList = useSelector(({contacts}) =>
		contacts?.entities?.length > 0 ? normalizeContactList(getAvailableEntityList(contacts.entities, false)) : []
	);
	const sizeLarge = 'L';
	const weightLight = 'light';
	const weightBold = 'bold';

	const renderOutputText = (text, light = false) => {
		return (
			<OutputText body2 light={light}>
				{text}
			</OutputText>
		);
	};

	// If the user expands the row for a message, they should see rows with the following labels (in this order, if displayed):
	// "Contacts" - Show a list of contacts the message was sent to in alphanumeric order by name.  Include first and last name for each.
	// "Entities" - Show the selected entities that the message was sent to (if applicable) in alphanumeric order by name.
	// "Countries" - Show the selected countries that the message was sent to (if applicable) in alphanumeric order by name.
	// "Group roles" - Show the selected group roles that the message was sent to (if applicable) in alphanumeric order by name.
	// "Engagements" - Show the selected engagements that the message was sent to (if applicable) in alphanumeric order by name.
	// "Service lines" - Show the selected service lines that the message was sent to (if applicable) in alphanumeric order by name.
	// "Roles" - Show the selected roles that the message was sent to (if applicable) in alphanumeric order by name.
	// If a row is not applicable to a message, then do not display it.
	// If there are more contents for a message than can fit in a row, then the user should see a "See more" option.
	// If the user selects this option, then they should see the additional rows of contents.
	// The user should see a "See less" option and should be able to select to hide the additional rows of content.

	const renderRow = (label, list) => {
		const labelValue = renderOutputText(label, true);

		const renderList = list.join(', ');

		return (
			<>
				{labelValue}
				<SeeMore noOfLines={1}>{renderOutputText(renderList)}</SeeMore>
			</>
		);
	};

	const renderContacts = () => {
		if (!contactsList?.length) return null;
		const contactLabel = contactsList.length > 1 ? labels.contacts : labels.singularContact;
		const actualList = contactsList.map(({firstName, lastName}) => `${firstName} ${lastName}`);
		return renderRow(contactLabel, actualList);
	};

	const renderCountries = () => {
		if (!countriesList?.length) return null;
		const countryLabel = countriesList.length > 1 ? labels.countries : labels.country;
		return renderRow(
			countryLabel,
			countriesList.map(({name}) => name)
		);
	};

	const renderGroupRoles = () => {
		if (!groupRolesList?.length) return null;
		const groupRoleLabel = groupRolesList.length > 1 ? labels.groupRoles : labels.groupRole;
		return renderRow(
			groupRoleLabel,
			groupRolesList.map(({name}) => name)
		);
	};

	const renderEngagements = () => {
		if (!engagementsList?.length) return null;
		const engagementLabel = engagementsList.length > 1 ? labels.engagementsLabel : labels.engagementLabel;
		return renderRow(
			engagementLabel,
			engagementsList.map(({name}) => name)
		);
	};

	const renderServiceLine = () => {
		if (!serviceLineList?.length) return null;
		const serviceLineLabel = serviceLineList.length > 1 ? labels.serviceLines : labels.serviceLine;
		return renderRow(
			serviceLineLabel,
			serviceLineList.map(({name}) => name)
		);
	};

	const renderRoles = () => {
		if (!rolesList?.length) return null;
		const roleLabel = rolesList.length > 1 ? labels.roles : labels.role;
		return renderRow(
			roleLabel,
			rolesList.map(({name}) => name)
		);
	};

	const renderEntities = () => {
		if (!entitiesList?.length) return null;
		const entityLabel = entitiesList.length > 1 ? labels.entitiesLabels : labels.entityLabel;
		return renderRow(
			entityLabel,
			entitiesList.map((e) => e.entityName)
		);
	};

	const renderAccordionContent = () => {
		const subjectTitle = renderOutputText(labels.subject, sizeLarge, weightLight, 'subjectAccordion');
		const subjectValue = renderOutputText(subject, sizeLarge, weightBold, 'subject-values');

		return (
			<MotifAccordionContent>
				<section className="accordion-container">
					{renderContacts()}
					{renderEntities()}
					{renderCountries()}
					{renderGroupRoles()}
					{renderEngagements()}
					{renderServiceLine()}
					{renderRoles()}
					{subjectTitle}
					{subjectValue}
				</section>
			</MotifAccordionContent>
		);
	};

	return <StyledMessageAccordionContent>{renderAccordionContent()}</StyledMessageAccordionContent>;
};

export default MessageAccordionContent;
